import { z } from 'zod';
import { ModelProfile } from './llm.js';

/**
 * Prompts are managed assets (07-PROMPT-ENGINE): versioned, DRAFT → TESTING → PUBLISHED →
 * DEPRECATED, immutable once published. Templates are logic-less: `{{name}}` placeholders
 * only. Variables marked `untrusted` are inserted inside a labelled data block and never as
 * instructions (prompt-injection mitigation).
 */
export const PromptVariable = z.object({
  name: z.string().regex(/^[a-z][a-zA-Z0-9_]{0,40}$/),
  type: z.enum(['string', 'json']),
  required: z.boolean().default(true),
  /** Content from external systems (issues, documents...). Rendered as delimited data. */
  untrusted: z.boolean().default(false),
  description: z.string().max(300).default(''),
  /** Upper bound for the rendered value (characters). */
  maxLength: z.number().int().positive().max(200_000).default(20_000),
});
export type PromptVariable = z.infer<typeof PromptVariable>;

export const PromptStatus = z.enum(['draft', 'testing', 'published', 'deprecated']);
export type PromptStatus = z.infer<typeof PromptStatus>;

export const PromptSeed = z.object({
  key: z.string().regex(/^[a-z][a-z0-9]*(\.[a-z0-9_]+)+$/),
  name: z.string().min(1).max(120),
  description: z.string().max(1000).default(''),
  domain: z.string().regex(/^[a-z][a-z0-9-]*$/),
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  systemTemplate: z.string().min(1).max(50_000),
  userTemplate: z.string().min(1).max(50_000),
  variables: z.array(PromptVariable).max(30),
  /** Named output schema ("ns.Name@1") the model output must satisfy. */
  outputSchemaRef: z.string().regex(/^[a-z][a-z0-9]*(\.[A-Za-z0-9_]+)+@\d+$/),
  modelProfile: ModelProfile,
  changeNote: z.string().max(500).default(''),
});
export type PromptSeed = z.infer<typeof PromptSeed>;

export const PromptVersionView = z.object({
  id: z.string().uuid(),
  version: z.string(),
  status: PromptStatus,
  systemTemplate: z.string(),
  userTemplate: z.string(),
  variables: z.array(PromptVariable),
  outputSchemaRef: z.string(),
  modelProfile: z.string(),
  changeNote: z.string(),
  checksum: z.string(),
  createdAt: z.iso.datetime(),
  publishedAt: z.iso.datetime().nullable(),
});
export type PromptVersionView = z.infer<typeof PromptVersionView>;

export const PromptView = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  domain: z.string(),
  scope: z.enum(['platform', 'organization']),
  versions: z.array(PromptVersionView),
});
export type PromptView = z.infer<typeof PromptView>;

export const PromptStatusChange = z.object({
  status: z.enum(['testing', 'published', 'deprecated', 'draft']),
});
